/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { useResetPageOnFilter } from "hooks/tableReset.hook";
import {
  AccountingMovementInterface,
  PaymentInterface,
  HeaderEnum,
  QueryResultInterface,
} from "medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import { formatDateForAPI } from "utils/date.utils";

/**
 * Properties for the PaymentTable component.
 * 
 * @interface PaymentTableProps
 * @property {string} accountingId - Patient accounting ID for filtering payments
 * @property {AccountingMovementInterface[]} modelList - Array of accounting movements (payments)
 * @property {Function} setModelList - Callback to update payment list
 * @property {Function} [onViewPayment] - Optional callback when viewing payment details
 * @property {Date} [startDate] - Optional start date filter
 * @property {Date} [endDate] - Optional end date filter
 * @property {string} [canSearchPlaceholder] - Optional search placeholder text
 * @property {Function} [setModelFilter] - Optional external filter state setter
 */
interface PaymentTableProps {
  accountingId: string;
  modelList: AccountingMovementInterface[];
  setModelList: React.Dispatch<React.SetStateAction<AccountingMovementInterface[]>>;
  onViewPayment?: (payment: PaymentInterface) => void;
  startDate?: Date;
  endDate?: Date;
  canSearchPlaceholder?: string;
  setModelFilter?: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Payment table component for displaying patient payment transactions.
 * 
 * Features:
 * - Displays payment records with pagination
 * - Date range filtering (start/end dates)
 * - Search/filter functionality with external state sync
 * - Auto-loads payments on mount and filter changes
 * - View payment details callback support
 * - Server-side pagination with page size control
 * - Automatic page reset on filter change
 * - Safe array handling
 * 
 * Pagination:
 * - Default page size: 10 records
 * - Tracks total pages and rows
 * - Resets to page 1 on filter change
 * 
 * Filtering:
 * - Search by payment details
 * - Date range filtering (formatted for API)
 * - External filter state synchronization
 * 
 * @param {PaymentTableProps} props - Component properties
 * @returns {JSX.Element} Paginated payment table with filtering
 */
const PaymentTable: React.FC<PaymentTableProps> = ({
  accountingId,
  modelList,
  setModelList,
  onViewPayment,
  startDate,
  endDate,
  canSearchPlaceholder,
  setModelFilter: externalSetModelFilter,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<string>("");
  const [isLoading, _error, sendRequest] = useHttp();

  useResetPageOnFilter(filterModel, page, setPage);

  const handleFilterChange = useCallback((filter: React.SetStateAction<string>) => {
    const filterValue = typeof filter === 'function' ? filter(filterModel) : filter;
    setFilterModel(filterValue);
    if (externalSetModelFilter) {
      externalSetModelFilter(filter);
    }
  }, [externalSetModelFilter, filterModel]);

  const loadPayments = useCallback(() => {
    if (accountingId) {
      const searchParam = filterModel ? `search=${encodeURIComponent(filterModel)}&` : "";
      const startDateParam = startDate ? `startDate=${formatDateForAPI(startDate, false)}&` : "";
      const endDateParam = endDate ? `endDate=${formatDateForAPI(endDate, true)}&` : "";
      const paginationParams = `page=${page}&pageSize=${pageSize}`;
      
      const url = `${CoreAPI_URLs.PAYMENT_GET_BY_ACCOUNTING_MOVEMENTS}?${startDateParam}${endDateParam}${searchParam}${paginationParams}`;

      const headers = [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountingId }];

      sendRequest<QueryResultInterface<AccountingMovementInterface>, void>(
        url,
        (result) => {
          const paymentsArray = Array.isArray(result) ? result : result.results || [];
          const total = Array.isArray(result) ? result.length : result.total || 0;
          
          setModelList(paymentsArray);
          setRowsTotal(total);
          setPageTotal(Math.ceil(total / pageSize));
        },
        "GET",
        undefined,
        headers
      );
    }
  }, [accountingId, page, pageSize, filterModel, startDate, endDate, sendRequest, setModelList]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);

  const getUITable = (list: AccountingMovementInterface[]) => ({
    columns: [
      {
        Header: t("paymentTable.patient"),
        accessor: "accounting.patient",
        sortIndicatorType: "az",
        Cell: ({ value }: { value: any }) => {
          const fullName =
            `${value.person.name + " " + value.person.lastname}` || t("paymentTable.noPatient");
          return <DefaultCell value={fullName} />;
        },
      },
      {
        Header: t("paymentTable.payer"),
        accessor: "payment.payer",
        sortIndicatorType: "az",
        Cell: ({ row }: any) => {
          const payment: PaymentInterface | undefined = row.original.payment;
          const payer = payment?.payer;
          const payerName =
            payer && (payer as any).name
              ? `${(payer as any).name} ${(payer as any).lastname || ""}`.trim()
              : t("paymentTable.noPayer");
          return <DefaultCell value={payerName} />;
        },
      },
      {
        Header: t("paymentTable.paymentMethod"),
        accessor: "payment.paymentMethod",
        sortIndicatorType: "az",
        Cell: ({ row }: any) => {
          const payment: PaymentInterface | undefined = row.original.payment;
          return <DefaultCell value={payment ? t(`accountingsTable.${payment.paymentMethod}`) : ""} />;
        },
      },
      {
        Header: t("paymentTable.folio"),
        accessor: "payment.folio",
        sortIndicatorType: "numeric",
        Cell: ({ row }: any) => {
          const payment: PaymentInterface | undefined = row.original.payment;
          return <DefaultCell value={payment ? payment.folio : ""} />;
        },
      },
      {
        Header: t("paymentTable.posted"),
        accessor: "payment.posted",
        sortIndicatorType: "numeric",
        Cell: ({ row }: any) => {
          const payment: PaymentInterface | undefined = row.original.payment;
          return <DefaultCell value={payment ? "$ " + String(payment.posted) + " USD" : ""} />;
        },
      },
      {
        Header: t("paymentTable.actions"),
        accessor: "payment.id",
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const payment: PaymentInterface | undefined = row.original.payment;
          return (
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                if (payment && onViewPayment) {
                  onViewPayment(payment);
                }
              }}
            >
              {t("paymentTable.viewAction")}
            </Button>
          );
        },
      },
    ],
    rows: list,
  });

  const tableData = useMemo(() => getUITable(modelList), [modelList, t]);

  return (
    <Box width="100%">
      <DataTable 
        table={tableData} 
        canSearch={true}
        canSearchPlaceholder={canSearchPlaceholder || t("paymentTable.searchPayment")}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        setModelFilter={handleFilterChange}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default PaymentTable;